/**
 * Skin Complete — Scroll Reveal + Count-up
 * Keeps motion minimal, premium, and non-distracting.
 */
(() => {
  'use strict';

  const prefersReducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  /* =============================================================
     1. SCROLL REVEAL
     ============================================================= */
  const SELECTOR_ITEM  = '[data-sc-reveal]';
  const SELECTOR_GROUP = '[data-sc-reveal-group]';
  const ATTR_VISIBLE   = 'data-sc-visible';
  const STAGGER_MS     = 90;

  const markAllVisible = () => {
    document.querySelectorAll(SELECTOR_ITEM).forEach((el) => el.setAttribute(ATTR_VISIBLE, ''));
  };

  if (prefersReducedMotion || typeof IntersectionObserver === 'undefined') {
    markAllVisible();
  } else {
    const reveal = (el, index = 0) => {
      if (el.hasAttribute(ATTR_VISIBLE)) return;
      const delay = index * STAGGER_MS;
      if (delay > 0) el.style.transitionDelay = `${delay}ms`;
      requestAnimationFrame(() => el.setAttribute(ATTR_VISIBLE, ''));
    };

    const revealObserver = new IntersectionObserver(
      (entries, obs) => {
        for (const entry of entries) {
          if (!entry.isIntersecting) continue;
          const target = entry.target;
          const group  = target.closest(SELECTOR_GROUP);
          if (group && target.matches(SELECTOR_ITEM)) {
            const siblings = Array.from(group.querySelectorAll(SELECTOR_ITEM));
            const idx = siblings.indexOf(target);
            reveal(target, Math.max(0, idx));
          } else {
            reveal(target);
          }
          obs.unobserve(target);
        }
      },
      { threshold: 0.12, rootMargin: '0px 0px -8% 0px' }
    );

    const scanReveals = (root = document) => {
      root.querySelectorAll(SELECTOR_ITEM).forEach((el) => {
        if (!el.hasAttribute(ATTR_VISIBLE)) revealObserver.observe(el);
      });
    };

    document.addEventListener('DOMContentLoaded', () => scanReveals(), { once: true });
    if (document.readyState !== 'loading') scanReveals();
    document.addEventListener('shopify:section:load',    (e) => scanReveals(e.target));
    document.addEventListener('shopify:section:reorder', () => scanReveals());
  }

  /* =============================================================
     2. COUNT-UP NUMBERS
     Elements keep their original textContent as the target value.
     ============================================================= */
  const easeOutQuart = (t) => 1 - Math.pow(1 - t, 4);

  const animateCount = (el, target, duration = 1800) => {
    const hasDecimal = /\./.test(String(target));
    const to = Number(String(target).replace(',', '.'));
    if (!isFinite(to)) return;
    const start = performance.now();

    const tick = (now) => {
      const t = Math.min(1, (now - start) / duration);
      const eased = easeOutQuart(t);
      const value = to * eased;
      el.textContent = hasDecimal
        ? value.toFixed(1).replace('.', ',')
        : Math.round(value).toString();
      if (t < 1) requestAnimationFrame(tick);
      else el.textContent = String(target);
    };
    requestAnimationFrame(tick);
  };

  const initCounters = () => {
    const counters = document.querySelectorAll('[data-sc-count]');
    if (!counters.length) return;

    if (prefersReducedMotion || typeof IntersectionObserver === 'undefined') {
      counters.forEach((el) => { /* keep static textContent */ });
      return;
    }

    const countObserver = new IntersectionObserver(
      (entries, obs) => {
        for (const entry of entries) {
          if (!entry.isIntersecting) continue;
          const el = entry.target;
          const target = el.getAttribute('data-sc-count-to') || el.textContent.trim();
          el.setAttribute('data-sc-count-to', target);
          el.textContent = '0';
          animateCount(el, target, 1800);
          obs.unobserve(el);
        }
      },
      { threshold: 0.4 }
    );

    counters.forEach((el) => {
      if (!el.hasAttribute('data-sc-count-to')) {
        el.setAttribute('data-sc-count-to', el.textContent.trim());
      }
      countObserver.observe(el);
    });
  };

  /* =============================================================
     BOOT
     ============================================================= */
  const boot = () => {
    initCounters();
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot, { once: true });
  } else {
    boot();
  }

  document.addEventListener('shopify:section:load', boot);
})();
